import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import {
  PRIVATE_INCLUDED_USPS,
  PRIVATE_ASSISTANT_GROUPS,
  PRIVATE_UNLOCKED_USPS,
} from '../../data/privateUsps';
import { USP_PROVIDERS, isFuelXDirect } from '../../data/uspProviders';
import USPInfoIcon from '../shared/USPInfoIcon';

/** USP-Key → bereinigtes Card-Bild (ohne eingebrannten Text) */
const CLEAN_IMG = {
  'fuel-discount': '/tanken-guenstiger-960.webp',
  'error-diagnosis': '/usp/clean/card-01-fehlerdiagnose.webp',
  'repair-transparency': '/usp/clean/card-02-reparaturkosten.webp',
  'damage-photo-check': '/usp/clean/card-22-schadenfoto-check.webp',
  'breakdown-first-aid': '/usp/clean/card-23-pannen-soforthilfe.webp',
  'usedcar-history-check': '/usp/clean/card-17-gebrauchtwagen-check.webp',
  'kasko-support': '/usp/clean/card-03-kasko-selbstbehalt.webp',
  'insurance-optimize': '/usp/clean/card-04-kfz-versicherung.webp',
  'credit-optimize': '/usp/clean/card-05-autokredit.webp',
  'sell-service': '/usp/clean/card-07-auto-verkaufen.webp',
  'vehicle-valuation': '/usp/clean/card-08-fahrzeugbewertung.webp',
  'hotel-prices': '/usp/clean/card-09-hotels.webp',
  'insurance-refund': '/usp/clean/card-10-kfz-versicherung-zurueck.webp',
  'tuev-refund': '/usp/clean/card-11-tuev-hu.webp',
  'breakdown-refund': '/usp/clean/card-12-pannenhilfe.webp',
  'tax-refund': '/usp/clean/card-13-kfz-steuer.webp',
  'oilchange-refund': '/usp/clean/card-14-oelwechsel.webp',
  'tirechange-refund': '/usp/clean/card-15-reifenwechsel.webp',
  'detailing-refund': '/usp/clean/card-16-fahrzeugaufbereitung.webp',
  'usedcar-check': '/usp/clean/card-17-gebrauchtwagen-check.webp',
  'valuation-refund': '/usp/clean/card-18-wertgutachten.webp',
  'dent-repair': '/usp/clean/card-19-dellenreparatur.webp',
  'glass-repair': '/usp/clean/card-20-glasreparatur.webp',
  'leasing-return': '/usp/clean/card-21-leasing-rueckgabe.webp',
};

/** USP-Key → Dienstleister-Logo-Slug */
const PROV_MAP = {
  'fuel-discount': 'tankrabatt',
  'error-diagnosis': 'fehlerdiagnose',
  'repair-transparency': 'reparaturkosten-check',
  'damage-photo-check': 'schadenfoto-check',
  'breakdown-first-aid': 'pannen-soforthilfe',
  'usedcar-history-check': 'gebrauchtwagen-historiencheck',
  'kasko-support': 'kasko-selbstbehalt',
  'insurance-optimize': 'kfz-versicherung-optimieren',
  'credit-optimize': 'autokredit-optimieren',
  'sell-service': 'auto-verkaufen-service',
  'vehicle-valuation': 'fahrzeugbewertung',
  'hotel-prices': 'hotels-16-mio',
  'insurance-refund': 'kfz-versicherung-zurueck',
  'tuev-refund': 'tuev-hu-zurueck',
  'breakdown-refund': 'pannenhilfe-zurueck',
  'tax-refund': 'kfz-steuer',
  'oilchange-refund': 'oelwechsel-zurueck',
  'tirechange-refund': 'reifenwechsel-zurueck',
  'detailing-refund': 'fahrzeugaufbereitung-zurueck',
  'usedcar-check': 'gebrauchtwagen-check',
  'valuation-refund': 'wertgutachten-zurueck',
  'dent-repair': 'dellenreparatur-zurueck',
  'glass-repair': 'glasreparatur-zurueck',
  'leasing-return': 'leasing-rueckgabe',
  'service-reminders': 'service-erinnerungen',
  'km-maintenance-plan': 'wartungsplan',
  'recall-check': 'rueckruf-check',
  'tire-season-manager': 'saisonmanager',
  'quote-check': 'angebotscheck',
  'invoice-check': 'rechnungscheck',
  'value-history': 'wert-verlauf',
  'insurance-deadline': 'fristenwaechter',
  'usedcar-docs-check': 'unterlagencheck',
  'leasing-planner': 'leasing-planer',
  'car-cost-check': 'autokosten-check',
  'vehicle-folder': 'fahrzeugordner',
  'accident-assistant': 'unfall-assistent',
};

/** Kurze Painpoint-Texte für die Cards (die langen Info-Texte bleiben im Konfigurator erhalten) */
const CARD_TEXT = {
  'fuel-discount': 'Spritpreise hoch, Budget knapp? Dein FuelGood Guthaben senkt deinen effektiven Literpreis um Ø 0,40 € — markenunabhängig an 150.000+ Tankstellen.',
  'error-diagnosis': 'Warnleuchte an und keine Ahnung, warum? Du bekommst sofort eine Experten-Ersteinschätzung — bevor es teuer wird.',
  'repair-transparency': 'Angst, über den Tisch gezogen zu werden? Wir prüfen Angebot und Preis — du zahlst nur, was fair ist.',
  'damage-photo-check': 'Unfall, Kratzer, Delle oder Steinschlag entdeckt? Foto hochladen — du erhältst sofort eine kostenlose transparente Ersteinschätzung.',
  'usedcar-history-check': 'Versteckte Mängel beim Gebrauchten? Du bekommst 3 Historien-Reports pro Jahr — Vorschäden, Tacho, Diebstahl.',
  'kasko-support': 'Der Selbstbehalt macht den Schaden noch teurer? Bis zu 500 € können übernommen werden.',
  'insurance-optimize': 'Zahlst du zu viel für deine Kfz-Versicherung? Police hochladen — ein Angebot kommt nur bei echter Ersparnis.',
  'credit-optimize': 'Autokredit zu teuer? Kredit hochladen — bessere Zinsen kommen direkt von der Bank.',
  'sell-service': 'Keine Lust auf Verkaufsstress und Händlerabschlag? Wir übernehmen alles — für 499 € statt 1.199 €.',
  'vehicle-valuation': 'Ist das Händlerangebot wirklich fair? Du erhältst den ehrlichen Marktwert, bevor du verkaufst.',
  'breakdown-first-aid': 'Auto springt nicht an oder falsch getankt? Du bekommst sofort erste Hilfe und die passende Vermittlung — bestehende Mitgliedschaft bei einem Pannenhilfe-Dienstleister vorausgesetzt.',
  'hotel-prices': 'Zu viel fürs Hotel bezahlt? Über FuelX bekommst du den Bestpreis auf rund 1,6 Mio. Unterkünfte — und Buchungen schalten nebenbei KFZ-Vorteile frei.',
  'service-reminders': 'HU verpasst und draufgezahlt? Wir erinnern dich automatisch an TÜV, Inspektion, Öl und Reifen.',
  'km-maintenance-plan': 'Was wird als Nächstes fällig? Deine Kilometer verraten es — zugeschnitten auf dein Fahrprofil.',
  'recall-check': 'Gibt es Rückrufe für dein Fahrzeug? Wir gleichen es mit der offiziellen KBA-Datenbank ab.',
  'tire-season-manager': 'Reifenwechsel wieder zu spät? Zweimal im Jahr automatisch erinnert — inklusive Profiltiefe und Einlagerung.',
  'car-cost-check': 'Was kostet dich dein Auto wirklich? Alle Kosten monatlich und jährlich aufgeschlüsselt.',
  'quote-check': 'Ist der Kostenvoranschlag überteuert? Hochladen — wir prüfen Preise und unnötige Positionen vor der Reparatur.',
  'invoice-check': 'Rechnung höher als angekündigt? Wir prüfen jede unklare Position — verständlich erklärt.',
  'vehicle-folder': 'Unterlagen nie auffindbar? Rechnungen, Policen und Dokumente — digital an einem Ort.',
  'value-history': 'Wann ist der beste Verkaufszeitpunkt? Beobachte, wie sich dein Fahrzeugwert entwickelt.',
  'insurance-deadline': 'Kündigungsfrist wieder verpasst? Wir erinnern dich rechtzeitig vor dem Stichtag.',
  'usedcar-docs-check': 'Sind die Unterlagen des Gebrauchten sauber? Wir prüfen Serviceheft, HU-Berichte und Vorbesitzer.',
  'accident-assistant': 'Unfall — und jetzt? Schritt für Schritt begleitet, alles dokumentiert im Fahrzeugordner.',
  'leasing-planner': 'Angst vor der Leasing-Nachzahlung? Countdown, Checkliste und Foto-Doku — rechtzeitig vorbereitet.',
  'insurance-refund': 'Jedes Jahr zahlen — nie etwas zurück? Hol dir bis zu 750 € deines Kfz-Versicherungsbeitrags zurück.',
  'tuev-refund': 'Pflichttermin TÜV belastet das Budget? Bis zu 160 € HU-Kosten zurück auf deine Prepaid Card.',
  'breakdown-refund': 'Schutzbrief zahlen, aber nie brauchen? Bis zu 240 € deines Beitrags zurück.',
  'tax-refund': 'Die Steuer-Rechnung kommt jedes Jahr automatisch? Hol dir bis zu 450 € Kfz-Steuer zurück.',
  'oilchange-refund': 'Ölwechsel schon wieder fällig? Bis zu 450 € der Kosten zurück — Wartung wird belohnt.',
  'tirechange-refund': 'Zweimal im Jahr Reifen zahlen? Bis zu 250 € für Wechsel oder Montage zurück.',
  'detailing-refund': 'Aufbereitung lohnt sich — aber kostet? Bis zu 450 € für professionelle Fahrzeugaufbereitung zurück.',
  'usedcar-check': 'Ist das Schnäppchen wirklich eins? Bis zu 150 € für den Profi-Check vor dem Kauf zurück.',
  'valuation-refund': 'Verkaufst du unter Wert? Bis zu 300 € für ein ausführliches Wertgutachten zurück.',
  'dent-repair': 'Kleine Delle, großer Wertverlust? Bis zu 500 € für professionelle Dellenreparatur zurück.',
  'glass-repair': 'Steinschlag in Sekunden, Rechnung für immer? Bis zu 500 € für Glasreparatur zurück.',
  'leasing-return': 'Teure Überraschung bei der Rückgabe? Bis zu 500 € für die professionelle Vorbereitung.',
};

const getImg = (key) => CLEAN_IMG[key] || null;
const getProviders = (key) => {
  const slug = PROV_MAP[key];
  return slug ? (USP_PROVIDERS[slug] || []).slice(0, 3) : [];
};

const ASSISTANT_ITEMS = PRIVATE_ASSISTANT_GROUPS.flatMap((g) =>
  g.items.map((item) => ({ ...item, groupLabel: g.label }))
);

const TABS = [
  {
    key: 'inklusive',
    label: 'Inklusive Leistungen',
    items: PRIVATE_INCLUDED_USPS.map((u) => ({ ...u, chip: u.badge })),
    desc: 'Ab Tag 1 an deiner Seite — Experten-Hilfe von der Warnleuchte bis zum Autoverkauf, im Jahresbeitrag enthalten.',
  },
  {
    key: 'assistent',
    label: 'Fahrzeug-Assistent',
    items: ASSISTANT_ITEMS.map((u) => ({ ...u, chip: u.groupLabel })),
    desc: '13 digitale Services, die für dein Auto mitdenken — Termine, Kosten, Dokumente und Hilfe unterwegs.',
  },
  {
    key: 'hotels',
    label: 'Mit FuelX Hotels',
    items: PRIVATE_UNLOCKED_USPS.map((u) => ({
      ...u,
      chip: u.maxEur ? `Bis zu ${u.maxEur.toLocaleString('de-DE')} € zurück` : 'Freischaltbar',
    })),
    desc: 'Und nebenbei: Qualifizierende Hotelbuchungen schalten zusätzliche KFZ-Vorteile frei — von der Kfz-Steuer bis zum Reifenwechsel.',
  },
];

function BenefitCard({ item, number, testId, compact = false }) {
  const Icon = item.icon;
  const img = getImg(item.key);
  const providers = getProviders(item.key);
  const infoTrigger = (
    <USPInfoIcon
      variant="pill"
      dark
      title={item.title}
      badge={item.chip}
      text={item.info || CARD_TEXT[item.key]}
      painpoints={item.painpoints}
      testId={`showcase-info-${item.key}`}
    />
  );
  return (
    <article
      className={`relative ${compact ? 'w-[290px] sm:w-[320px]' : 'w-[330px] sm:w-[400px]'} shrink-0 snap-start rounded-[28px] ring-1 ring-emerald-300/25 overflow-hidden flex flex-col shadow-[0_20px_56px_-26px_rgba(2,44,34,0.65)] hover:ring-emerald-300/60 hover:-translate-y-1 transition-[transform,box-shadow] duration-300`}
      style={{ background: 'linear-gradient(135deg, #022c22 0%, #064e3b 58%, #047857 135%)' }}
      data-testid={testId}
    >
      {/* Glows + Akzentlinien (wie „Für mehr Auto") */}
      <div aria-hidden className="absolute -top-16 -right-14 w-56 h-56 rounded-full bg-emerald-400/20 blur-3xl pointer-events-none z-[1]" />
      <div aria-hidden className="absolute -bottom-20 -left-14 w-48 h-48 rounded-full bg-teal-300/10 blur-2xl pointer-events-none z-[1]" />
      <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-300/70 to-transparent z-[2]" />
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-emerald-300/40 to-transparent z-[2]" />

      {/* Konfigurator-Bild zum USP + auffälliger Info-Button oben links */}
      {img && (
        <div className={`relative ${compact ? 'h-[140px] sm:h-[150px]' : 'h-[165px] sm:h-[185px]'} overflow-hidden`}>
          <img
            src={img}
            srcSet={img.includes('/usp/clean/') ? `${img.replace('.webp', '-640.webp')} 640w, ${img} 1024w` : undefined}
            sizes={img.includes('/usp/clean/') ? '(max-width: 640px) 92vw, 420px' : undefined}
            alt={item.title}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            onError={(e) => {
              const el = e.currentTarget;
              if (el.dataset.imgFallback !== '1') {
                // 1. Versuch: srcSet/sizes verwerfen und Basis-Bild laden
                el.dataset.imgFallback = '1';
                el.removeAttribute('srcset');
                el.removeAttribute('sizes');
                el.src = img;
              } else {
                // 2. Versuch fehlgeschlagen: kaputtes Bild ausblenden — grüner Karten-Hintergrund bleibt
                el.style.display = 'none';
              }
            }}
          />
          <div aria-hidden className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#033728] to-transparent" />
          <div className="absolute top-3 left-3 z-10">{infoTrigger}</div>
        </div>
      )}

      <div className={`relative z-[3] ${compact ? 'p-5' : 'p-6 sm:p-7'} flex flex-col flex-1`}>
        <span
          className="pointer-events-none select-none absolute -bottom-2 right-5 font-display text-[80px] leading-none text-white/[0.05]"
          aria-hidden="true"
        >
          {String(number).padStart(2, '0')}
        </span>

        <div className="relative flex flex-wrap items-start justify-between gap-x-3 gap-y-2.5 mb-4">
          <div className="flex items-center gap-2.5 flex-shrink-0">
            <div className={`rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center flex-shrink-0 ${img ? 'w-11 h-11 -mt-12 backdrop-blur-md shadow-[0_10px_24px_-10px_rgba(0,0,0,0.6)]' : 'w-14 h-14'}`}>
              <Icon className={`text-emerald-300 ${img ? 'w-5 h-5' : 'w-6 h-6'}`} strokeWidth={1.7} />
            </div>
            {!img && infoTrigger}
          </div>
          {item.chip && (
            <span className="mt-0.5 flex-shrink-0 max-w-full text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-300 bg-emerald-400/15 border border-emerald-400/25 rounded-full px-3 py-1.5 whitespace-nowrap">
              {item.chip}
            </span>
          )}
        </div>

        <h3 className="relative font-display text-[21px] sm:text-[23px] tracking-[-0.015em] text-white leading-[1.15] mb-3">
          {item.title}
        </h3>
        <p className="relative text-[13px] sm:text-[14px] text-emerald-100/80 leading-[1.7]">{CARD_TEXT[item.key] || item.info}</p>

        {/* Partner-Logos */}
        {providers.length > 0 && (
          <div className="relative mt-auto pt-6">
            <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-emerald-200/50 mb-2.5">
              {isFuelXDirect(providers)
                ? 'Direkt von FuelX — wir kümmern uns persönlich'
                : providers.some((p) => p.brand)
                  ? 'Markenunabhängig tanken — u. a.'
                  : 'Dienstleister, auf die wir zugreifen — u. a.'}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {providers.map((p) => (
                <span key={p.name} className="inline-flex items-center bg-white rounded-lg px-2.5 py-1.5 shadow-sm" title={p.name}>
                  <img src={p.logo} alt={p.name} className="h-3.5 w-auto max-w-[76px] object-contain" loading="lazy" />
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

/**
 * EmbeddedCardBenefits — kompakte Variante der „Für mehr Auto"-Karten (dunkelgrün, mit Bildern,
 * horizontal scrollbar). Für Konfigurator & Warenkorb-Drawer, ohne die große Landing-Section.
 */
export function EmbeddedCardBenefits({ testId = 'embedded-benefits' }) {
  const [active, setActive] = useState('inklusive');
  const trackRef = useRef(null);
  const tab = TABS.find((t) => t.key === active);

  useEffect(() => { trackRef.current?.scrollTo({ left: 0 }); }, [active]);

  const scrollTrack = (dir) => {
    const el = trackRef.current;
    if (el) el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.9), behavior: 'smooth' });
  };

  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700">
          37 Leistungen · dein Rundum-Schutz
        </p>
        <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0">
          <button type="button" onClick={() => scrollTrack(-1)} aria-label="Zurück scrollen"
            className="w-8 h-8 rounded-full border border-stone-200 bg-white text-stone-700 hover:bg-emerald-400 hover:border-emerald-400 hover:text-emerald-950 flex items-center justify-center transition-all" data-testid={`${testId}-prev`}>
            <ChevronLeft className="w-4 h-4" strokeWidth={2.2} />
          </button>
          <button type="button" onClick={() => scrollTrack(1)} aria-label="Weiter scrollen"
            className="w-8 h-8 rounded-full border border-stone-200 bg-white text-stone-700 hover:bg-emerald-400 hover:border-emerald-400 hover:text-emerald-950 flex items-center justify-center transition-all" data-testid={`${testId}-next`}>
            <ChevronRight className="w-4 h-4" strokeWidth={2.2} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 -mx-0.5 px-0.5" role="tablist" aria-label="FuelX Card Leistungen" data-testid={`${testId}-tabs`}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active === t.key}
            onClick={() => setActive(t.key)}
            className={`flex-shrink-0 px-3.5 py-2 rounded-full text-[12px] font-semibold border whitespace-nowrap transition-all ${
              active === t.key
                ? 'bg-emerald-400 text-emerald-950 border-emerald-400 shadow-neon'
                : 'bg-white text-stone-600 border-stone-200 hover:border-emerald-400/60'
            }`}
            data-testid={`${testId}-tab-${t.key}`}
          >
            {t.label}
            <span className={`ml-2 text-[11px] font-medium tabular-nums ${active === t.key ? 'text-emerald-900/70' : 'text-stone-400'}`}>{t.items.length}</span>
          </button>
        ))}
      </div>

      <p className="text-[12.5px] text-stone-500 leading-snug mt-2 mb-3" data-testid={`${testId}-desc`}>{tab.desc}</p>

      {/* Karten-Track — horizontal scrollbar, gesnappt */}
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 right-0 w-5 bg-gradient-to-l from-white to-transparent z-10" aria-hidden="true" />
        <div
          ref={trackRef}
          className="flex gap-4 items-stretch overflow-x-auto scrollbar-hide snap-x snap-mandatory py-1"
          data-testid={`${testId}-track`}
        >
          {tab.items.map((item, i) => (
            <BenefitCard key={item.key} item={item} number={i + 1} compact testId={`${testId}-card-${item.key}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * CardBenefitsShowcase — die 37 Leistungen der FuelX Card im „Für mehr Auto"-Design.
 * 3 Tabs (Inklusive · Fahrzeug-Assistent · FuelX Hotels), alle Karten nebeneinander —
 * frei scrollbar per Pfeil-Buttons, Swipe und Trackpad, damit man schnell alles sieht.
 */
export default function CardBenefitsShowcase() {
  const { openCart } = useCart();
  const [active, setActive] = useState('inklusive');
  const trackRef = useRef(null);
  const tab = TABS.find((t) => t.key === active);

  useEffect(() => {
    trackRef.current?.scrollTo({ left: 0 });
  }, [active]);

  const scrollTrack = (dir) => {
    const el = trackRef.current;
    if (el) el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.85), behavior: 'smooth' });
  };

  return (
    <section id="leistungen" className="py-20 sm:py-28 bg-white scroll-mt-24 overflow-hidden" data-testid="card-benefits-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-10 sm:mb-12">
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-emerald-600 mb-5">
            37 Leistungen · Ein Ansprechpartner
          </p>
          <h2 className="font-display text-stone-900 leading-[0.95] tracking-[-0.03em] text-4xl sm:text-5xl lg:text-6xl mb-6" data-testid="benefits-headline">
            Immer für dein Auto <span className="text-emerald-500">da.</span>
          </h2>
          <p className="text-lg sm:text-xl text-stone-500 leading-relaxed max-w-2xl">
            Warnleuchte, Werkstattrechnung, Panne, Versicherung oder Verkauf — mit 37 Leistungen
            steht FuelX dir zur Seite. Die meisten sind ab Tag 1 inklusive. Und nebenbei schalten
            Reisen zusätzliche KFZ-Vorteile frei.
          </p>
        </div>

        {/* Tabs — Desktop/Tablet (mobil sitzt die Leiste direkt über den Karten) */}
        <div className="hidden sm:flex flex-wrap gap-2.5 mb-5" role="tablist" aria-label="FuelX Card Leistungen">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active === t.key}
              onClick={() => setActive(t.key)}
              className={`px-6 py-3 rounded-full text-[15px] font-semibold border transition-all ${
                active === t.key
                  ? 'bg-emerald-400 text-emerald-950 border-emerald-400 shadow-neon'
                  : 'bg-white text-stone-600 border-stone-200 hover:border-emerald-400/60 hover:shadow-neon-sm'
              }`}
              data-testid={`benefits-tab-${t.key}`}
            >
              {t.label}
              <span className={`ml-2.5 text-[12px] font-medium tabular-nums ${active === t.key ? 'text-emerald-900/70' : 'text-stone-400'}`}>
                {t.items.length}
              </span>
            </button>
          ))}
        </div>
        <div className="flex items-end justify-between gap-6 mb-8 sm:mb-10">
          <p className="text-[15px] sm:text-base text-stone-500 max-w-2xl" data-testid="benefits-tab-desc">{tab.desc}</p>
          <div className="hidden sm:flex items-center gap-2.5 flex-shrink-0">
            <button
              type="button"
              onClick={() => scrollTrack(-1)}
              aria-label="Zurück scrollen"
              className="w-12 h-12 rounded-full border border-stone-200 bg-white text-stone-700 hover:bg-emerald-400 hover:border-emerald-400 hover:text-emerald-950 hover:shadow-neon flex items-center justify-center transition-all"
              data-testid="benefits-prev"
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={2.2} />
            </button>
            <button
              type="button"
              onClick={() => scrollTrack(1)}
              aria-label="Weiter scrollen"
              className="w-12 h-12 rounded-full border border-stone-200 bg-white text-stone-700 hover:bg-emerald-400 hover:border-emerald-400 hover:text-emerald-950 hover:shadow-neon flex items-center justify-center transition-all"
              data-testid="benefits-next"
            >
              <ChevronRight className="w-5 h-5" strokeWidth={2.2} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile: Tabs direkt über den Karten — beim Bilder-Swipen immer sichtbar */}
      <div
        className="sm:hidden flex gap-2 overflow-x-auto scrollbar-hide px-6 pb-3"
        role="tablist"
        aria-label="FuelX Card Leistungen"
        data-testid="benefits-tabs-mobile"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active === t.key}
            onClick={() => setActive(t.key)}
            className={`flex-shrink-0 px-4 py-2.5 rounded-full text-[13px] font-semibold border whitespace-nowrap transition-colors ${
              active === t.key
                ? 'bg-emerald-400 text-emerald-950 border-emerald-400 shadow-neon'
                : 'bg-white text-stone-600 border-stone-200'
            }`}
            data-testid={`benefits-tab-mobile-${t.key}`}
          >
            {t.label}
            <span className={`ml-2 text-[11px] font-medium tabular-nums ${active === t.key ? 'text-emerald-900/70' : 'text-stone-400'}`}>
              {t.items.length}
            </span>
          </button>
        ))}
      </div>

      {/* Alle Karten nebeneinander — fullbleed für „viel Leistung", sauber am Kartenraster gesnappt */}
      <div className="relative" data-testid="benefits-slider">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-white to-transparent z-10" aria-hidden="true" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-white to-transparent z-10" aria-hidden="true" />
        <div
          ref={trackRef}
          className="flex gap-5 sm:gap-6 items-stretch overflow-x-auto scrollbar-hide snap-x snap-mandatory py-2 px-6 scroll-pl-6"
          data-testid="benefits-track"
        >
          {tab.items.map((item, i) => (
            <BenefitCard key={item.key} item={item} number={i + 1} testId={`benefit-card-${item.key}`} />
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-6xl mx-auto px-6 lg:px-8 mt-12 flex flex-wrap items-center gap-5">
        <button
          type="button"
          onClick={() => openCart({ mode: 'private' })}
          className="inline-flex items-center gap-2.5 px-8 py-4 sm:px-9 sm:py-[18px] rounded-full bg-emerald-400 text-emerald-950 text-[15px] sm:text-base font-semibold hover:bg-emerald-300 transition-all group shadow-neon hover:shadow-neon-lg"
          data-testid="benefits-cta"
        >
          Jetzt Card sichern <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.4} />
        </button>
        <Link
          to="/vorteile"
          className="inline-flex items-center gap-2 text-[15px] sm:text-base font-semibold text-emerald-700 hover:text-emerald-900 transition-colors"
          data-testid="benefits-all-link"
        >
          Alle 37 Leistungen im Detail <ArrowRight className="w-5 h-5" strokeWidth={2} />
        </Link>
      </div>
    </section>
  );
}
